import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Flex,
  Grid,
  Icon,
  SimpleGrid,
  Skeleton,
  Text,
  VStack,
  HStack,
} from "@chakra-ui/react";
import { FaCamera, FaVideo } from "react-icons/fa";
import { MdVideocamOff } from "react-icons/md";
import { TimeIcon } from "@chakra-ui/icons";

import CctvCameraCard from "components/Cctv/CctvCameraCard";
import CctvMap from "components/Cctv/CctvMap";
import CctvStats from "components/Cctv/CctvStats";
import CctvSearchInput from "components/Cctv/CctvSearchInput";
import CctvFilterMenu from "components/Cctv/CctvFilterMenu";

import { useGetCctvListByLocationQuery } from "redux/services/cctv";
import type { CctvCamera } from "redux/services/cctv/types";
import { useCurrentTime } from "hooks/useCurrentTime";
import { useCameraLocationName } from "hooks/useCameraNameLocation";
import noData from "assets/images/empty-data.svg";

const CctvList: React.FC = () => {
  const navigate = useNavigate();
  const { parent_menu, menu: menuParam } = useParams<{
    parent_menu?: string;
    menu?: string;
  }>();

  // Mengambil prefix cctv- jika belum ada, hapus hyphen dari param
  const locationFilter = menuParam
    ? menuParam.startsWith("cctv-") ? menuParam : `cctv-${menuParam.replace(/-/g, "")}`
    : "";

  // Fetch CCTV Data based on menu param directly (as location)
  const { data: cctvData, isLoading } = useGetCctvListByLocationQuery(
    locationFilter,
    { skip: !menuParam }
  );

  // State untuk filter
  const [searchQuery, setSearchQuery] = useState("");
  const [showOnline, setShowOnline] = useState(true);
  const [showOffline, setShowOffline] = useState(true);
  const [selectedCamera, setSelectedCamera] = useState<CctvCamera | undefined>();
  const [showSuggestions, setShowSuggestions] = useState(false);

  const { currentTime, formatDate, formatTime } = useCurrentTime();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowSuggestions(value.length > 1);
  };

  const handleSuggestionClick = (name: string) => {
    setSearchQuery(name);
    setShowSuggestions(false);
  };

  // Stats
  const stats = useMemo(() => {
    const cameras = cctvData?.data || [];
    const total = cameras.length;
    const active = cameras.filter((cam: any) => cam.connection === 1).length;
    const offline = total - active;
    return { total, active, offline };
  }, [cctvData]);

  // Filter Logic
  const filteredCameras = useMemo(() => {
    let cameras = cctvData?.data || [];

    if (searchQuery) {
      cameras = cameras.filter(
        (cam: any) =>
          cam.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          cam.location.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    cameras = cameras.filter((cam: any) => {
      if (cam.connection === 1) return showOnline;
      return showOffline;
    });

    return cameras;
  }, [cctvData, searchQuery, showOnline, showOffline]);

  const handleCameraClick = (camera: CctvCamera) => {
    // url detail kamera
    navigate(`/apps/cctv/${parent_menu}/${menuParam}/${camera.id}`);
  };

  const handleMarkerClick = (camera: CctvCamera) => {
    setSelectedCamera(camera);
    const element = document.getElementById(`camera-${camera.id}`);
    element?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleResetFilters = () => {
    setShowOnline(true);
    setShowOffline(true);
    setSearchQuery("");
  };

  const { CameraLocationName } = useCameraLocationName();
  const title = menuParam ? CameraLocationName(menuParam) : "CCTV";

  return (
    <Box minH="100vh" p={5}>
      <VStack align="start" spacing={2} mb={4}>
        <Text fontSize="3xl" fontWeight="bold" marginBottom={0} textTransform="uppercase">
          {title}
        </Text>
        <Text color="gray.600" fontSize="sm">
          Menampilkan daftar kamera CCTV berdasarkan lokasi.
        </Text>
      </VStack>

      {/* Stats */}
      <Box bg="white" borderRadius="md" boxShadow="sm">
        <Skeleton isLoaded={!isLoading}>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
            <Flex
              bg="white"
              p={4}
              align="center"
              gap={3}
              borderRight="1px solid"
              borderColor="gray.200"
              roundedTopLeft="md"
            >
              <Flex
                bg="gray.50"
                p={3}
                borderRadius="md"
                align="center"
                justify="center"
              >
                <Icon as={TimeIcon} boxSize={6} color="gray.600" />
              </Flex>
              <Box>
                <Text fontSize="sm" color="gray.600">
                  {formatDate(currentTime)}
                </Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {formatTime(currentTime)}
                </Text>
              </Box>
            </Flex>
            <CctvStats
              icon={FaCamera}
              label="Total Kamera"
              value={stats.total}
              color="blue.500"
            />
            <CctvStats
              icon={FaVideo}
              label="Kamera Aktif"
              value={stats.active}
              color="green.500"
            />
            <CctvStats
              icon={MdVideocamOff}
              label="Kamera Offline"
              value={stats.offline}
              color="red.500"
            />
          </SimpleGrid>
        </Skeleton>
      </Box>

      {/* Content Grid */}
      <Grid
        bg="white"
        p={4}
        roundedBottomLeft="md"
        boxShadow="sm"
        borderTop="1px solid"
        pt={4}
        borderColor="gray.200"
        templateColumns={{ base: "1fr", lg: "1fr 350px" }}
        gap={6}
      >
        {/* Map */}
        <Box>
          <Skeleton isLoaded={!isLoading}>
            <CctvMap
              cameras={filteredCameras}
              selectedCamera={selectedCamera}
              onMarkerClick={handleMarkerClick}
              height="calc(100vh - 350px)"
            />
          </Skeleton>
        </Box>

        {/* List */}
        <Box p={4} border="1px solid" borderColor="gray.200" borderRadius="md">
          <VStack align="stretch" spacing={4}>
            {/* Filter */}
            <HStack spacing={2} align="center">
              <Box flex={1}>
                <CctvSearchInput
                  searchQuery={searchQuery}
                  handleSearchChange={handleSearchChange}
                  showSuggestions={showSuggestions}
                  setShowSuggestions={setShowSuggestions}
                  handleSuggestionClick={handleSuggestionClick}
                  cameras={cctvData?.data || []}
                />
              </Box>
              <CctvFilterMenu
                showOnline={showOnline}
                setShowOnline={setShowOnline}
                showOffline={showOffline}
                setShowOffline={setShowOffline}
                handleResetFilters={handleResetFilters}
              />
            </HStack>

            {/* Camera Items */}
            <Skeleton isLoaded={!isLoading}>
              <VStack
                align="stretch"
                spacing={3}
                maxH="calc(100vh - 400px)"
                overflowY="auto"
                pr={2}
                css={{
                  "&::-webkit-scrollbar": { width: "6px" },
                  "&::-webkit-scrollbar-track": {
                    background: "#f1f1f1",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#888",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": { background: "#555" },
                }}
              >
                {filteredCameras.length === 0 ? (
                  <Flex align="center" justify="center" h="full" flexDirection="column">
                    <img src={noData} alt="Empty Data" />
                    <Text textAlign="center" color="black" mb={2}>
                      Data tidak ditemukan
                    </Text>
                    <Text fontSize="xs" textAlign="center" color="gray.500" mb={0}>
                      Periksa kembali kata kunci pencarian Anda atau coba gunakan kata kunci yang berbeda
                    </Text>
                  </Flex>
                ) : (
                  filteredCameras.map((camera: any) => (
                    <Box key={camera.id} id={`camera-${camera.id}`}>
                      <CctvCameraCard
                        camera={camera}
                        onClick={() => handleCameraClick(camera)}
                      />
                    </Box>
                  ))
                )}
              </VStack>
            </Skeleton>
          </VStack>
        </Box>
      </Grid>
    </Box>
  );
};

export default CctvList;
